import { useEffect, useState } from 'react';

import { Entry } from 'contentful';
import { format } from 'date-fns';
import { NextPageWithLayout } from 'next';
import { ImageLoaderProps } from 'next/image';
import ContentLoader from 'react-content-loader';

import { BlogAuthor } from '../../../components/Blog/BlogAuthor/BlogAuthor';
import { BlogPostCarouselCard } from '../../../components/Blog/BlogPostCarouselCard/BlogPostCarouselCard';
import { BlogPostContent } from '../../../components/BlogPostContent/BlogPostContent';
import { Breadcrumbs } from '../../../components/Breadcrumbs/Breadcrumbs';
import { ContinuousCarousel } from '../../../components/Carousels/ContinuousCarousel/ContinuousCarousel';
import { InternalLink } from '../../../components/Clickables/InternalLink/InternalLink';
import { Container } from '../../../components/Container/Container';
import { DownloadAppHorizontalCTA } from '../../../components/CTAs/DownloadAppHorizontalCTA/DownloadAppHorizontalCTA';
import { DownloadAppVerticalCTA } from '../../../components/CTAs/DownloadAppVerticalCTA/DownloadAppVerticalCTA';
import { Divider } from '../../../components/Divider/Divider';
import { Grid } from '../../../components/Grid/Grid';
import { Heading } from '../../../components/Heading/Heading';
import { MetaTags } from '../../../components/MetaTags/MetaTags';
import { NextImageWrapper } from '../../../components/NextImage/NextImageWrapper';
import { SocialShareLinks } from '../../../components/SocialShareLinks/SocialShareLinks';
import { Text } from '../../../components/Text/Text';
import { useClientHasRendered } from '../../../helpers/hooks';
import {
  ContentfulImageFormatTypes,
  ContentfulResizeTypes,
  getContentfulLoader,
} from '../../../helpers/imageLoaders/contentful';
import { getRandomInteger } from '../../../helpers/number';
import { getBlogPostUrl } from '../../../helpers/urls';
import { TypeBlogPostSkeleton } from '../../../interfaces/contentful';

import styles from './BlogPost.module.css';
import { NearbyWaters } from './components/NearbyWaters/NearbyWaters';

export interface IBlogPostProps {
  post: Entry<TypeBlogPostSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>;
  relatedPosts: Entry<TypeBlogPostSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>[];
  latestPosts: Entry<TypeBlogPostSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>[];
  isPreview?: boolean;
}

const RANDOM_BLOGPOST_KEY = 'fishbrain-blog-post-unique-key-12345';

const ASPECTWIDTH = 360;
const ASPECTHEIGTH = 240; // aspect ratio = 67%

export const BlogPost: NextPageWithLayout<IBlogPostProps> = ({
  post,
  relatedPosts,
  latestPosts,
  isPreview = false,
}) => {
  const [randomAddCopyVarient, setRandomAddCopyVarient] = useState(0);
  const clientHasRendered = useClientHasRendered();

  const {
    publishDate,
    title,
    seoTitle,
    metaDescription,
    postDescription,
    headerImage,
    category,
    slug,
    author,
    body,
  } = post.fields;

  const categorySlug = category?.fields.slug;
  const categoryName = category?.fields.name;
  const { createdAt } = post.sys;

  const safeDate = clientHasRendered
    ? format(new Date(publishDate ?? createdAt), 'MMMM do, yyyy')
    : undefined;
  const blogPostUrl = categorySlug ? getBlogPostUrl(categorySlug, slug) : undefined;

  useEffect(() => {
    setRandomAddCopyVarient(getRandomInteger(1, 2));
  }, []);

  const contentfulLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getContentfulLoader(src, width, ASPECTWIDTH, ASPECTHEIGTH, {
      quality,
      resize: ContentfulResizeTypes.FILL,
    });

  const metaImageWidth = 1200;
  const metaImage = headerImage?.fields.file?.url
    ? getContentfulLoader(headerImage.fields.file.url, metaImageWidth, 1, 1, {
        format: ContentfulImageFormatTypes.PNG,
        resize: ContentfulResizeTypes.FILL,
      })
    : undefined;

  return (
    <>
      {isPreview && (
        <div className="mt-4 bg-pinktail p-4 text-center font-semibold text-white">
          In preview mode.
          <a className="underline" href="/api/exit-contentful-preview">
            Click here
          </a>{' '}
          to exit preview mode.
        </div>
      )}

      <Container className="mb-md">
        <MetaTags
          title={seoTitle ?? title}
          description={metaDescription || postDescription}
          image={metaImage}
          link={`/blog/${categorySlug}/${slug}`}
        />
        <Grid className={styles.postGrid}>
          <div className={`${styles.metadata} mt-8`}>
            <Breadcrumbs
              items={[
                { label: 'Blog', link: '/blog' },
                {
                  label: categoryName || '',
                  link: `/blog/${categorySlug}`,
                },
              ]}
            />
          </div>
          <div
            className={`${styles.title} prose prose-fishbrain place-self-start prose-lead:text-lg prose-lead:font-semibold`}
          >
            <Heading as="h1" variant="md" className="mb-4 mt-0">
              {title}
            </Heading>
            <Text variant="md" className="mb-4 mt-0 font-semibold">
              {safeDate ? (
                <time dateTime={safeDate}>{safeDate}</time>
              ) : (
                <ContentLoader height={24} uniqueKey={RANDOM_BLOGPOST_KEY} />
              )}
            </Text>
          </div>
          <div
            className={`${styles.paragraph} prose prose-fishbrain place-self-start prose-lead:text-lg prose-lead:font-semibold`}
          >
            <p className="lead m-0">{postDescription}</p>
            {blogPostUrl && (
              <SocialShareLinks source="blogPost" sourceId={slug} path={blogPostUrl} />
            )}
          </div>
          <div className={`${styles.image} relative w-full place-self-start md:place-self-center`}>
            {headerImage?.fields.file && (
              <NextImageWrapper
                wrapperClassName="pb-[67%]"
                customPlaceholderClassName="rounded-lg"
                imageClassName="rounded-inline-card"
                alt={title}
                src={headerImage.fields.file.url}
                loader={contentfulLoader}
                fill
                priority
                sizes="(min-width: 1680px) 515px, (min-width: 1280px) 33%, (min-width: 990px) calc(50vw), 100vw"
              />
            )}
          </div>
          <Divider className={`${styles.divider} my-sm`} />
          <div className={styles.post}>
            {body && (
              <BlogPostContent
                className="mb-8 prose-blockquote:border-l-[14px] prose-li:text-base"
                blogPost={body}
              />
            )}

            {author?.fields && <BlogAuthor author={author} />}
          </div>

          <div className={`${styles.cta} mt-6 block md:mt-0`}>
            <div
              className={`${styles.ctaInner} sticky flex h-fit flex-col gap-8 sm:flex-row md:flex-col`}
            >
              <DownloadAppVerticalCTA version={randomAddCopyVarient} />

              <div className="hidden sm:block">
                <Heading color="deep" className="mb-2" variant="sm">
                  Latest Posts
                </Heading>
                {latestPosts.map(latestP => {
                  const {
                    title: latestTitle,
                    category: latestCategory,
                    slug: latestSlug,
                  } = latestP.fields;
                  const latestUrl = latestCategory?.fields.slug
                    ? getBlogPostUrl(latestCategory.fields.slug, latestSlug)
                    : undefined;

                  return latestUrl ? (
                    <div key={latestTitle} className="mb-2">
                      <InternalLink
                        variant="textPrimary"
                        className="underline"
                        style={{ textDecorationSkipInk: 'none' }}
                        to={latestUrl}
                      >
                        {latestTitle}
                      </InternalLink>
                    </div>
                  ) : (
                    <></>
                  );
                })}
              </div>
            </div>
          </div>

          <DownloadAppHorizontalCTA className={`${styles.largeAppBanner} mt-10`} />
        </Grid>
      </Container>

      <div className="mt-10 sm:mt-16 md:mt-20">
        {relatedPosts.length > 0 && (
          <div className="pb-md sm:mt-18 bg-lake-fog pt-6 sm:pt-10 md:pt-14">
            <Container>
              <ContinuousCarousel
                dataTestId="relatedPosts"
                title="Related blog posts"
                childrenClassName="w-col-3 sm:w-col-6 md:w-col-4 min-w-col-3 sm:min-col-6 md:min-col-3"
              >
                {relatedPosts.map(relatedP => {
                  const {
                    slug: relatedSlug,
                    headerImage: relatedHimg,
                    title: relatedTitle,
                    postDescription: relatedDescription,
                    publishDate: relatedPublishDate,
                    subjectTags: relatedTages,
                    countryData: relatedCountryData,
                  } = relatedP.fields;

                  return relatedHimg?.fields.file && categorySlug ? (
                    <BlogPostCarouselCard
                      key={relatedSlug}
                      {...relatedP}
                      imageUrl={relatedHimg.fields.file.url}
                      slug={relatedSlug}
                      blogPostTitle={relatedTitle}
                      categorySlug={relatedSlug}
                      blogPostDescription={relatedDescription}
                      publishDate={relatedPublishDate}
                      clickEventSource="blogPost"
                      clickEventSourceId={slug}
                      blogTags={relatedTages}
                      country={relatedCountryData}
                    />
                  ) : (
                    <></>
                  );
                })}
              </ContinuousCarousel>
            </Container>
          </div>
        )}

        <NearbyWaters postSlug={slug} title="Fishing spots near you" />
      </div>
    </>
  );
};
